import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  Toolbar,
  ToolbarSection,
  ToolbarSpacer,
  ToolbarTitle,
} from 'designSystem/components/Toolbar'
import { PermissionsGate } from 'features/permissions'
import { ChartSaveAction } from 'features/timeSeries/components/chartActions/ChartSaveAction'
import { ExportPngButton } from 'features/timeSeries/components/chartActions/ExportPngButton'
import { MarkupToolButtons } from 'features/timeSeries/components/chartActions/MarkupToolButtons'
import { PenDisplayActions } from 'features/timeSeries/components/chartActions/PenDisplayActions'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { LayoutToggles } from 'features/workbench/LayoutToggles'
import { WorkbenchLayout } from 'features/workbench/types'
import { Waypoints } from 'lucide-react'
import React, { RefObject } from 'react'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

interface EmbeddedChartToolbarProps {
  leftOpen: boolean
  rightOpen: boolean
  modelsOpen: boolean
  onToggleLeft: () => void
  onToggleRight: () => void
  onToggleModels: () => void
  showRails: boolean
  canSave: boolean
  chartAreaRef: RefObject<HTMLDivElement>
}

/**
 * The one-row toolbar for an embedded chart: the Process Data workbench's
 * chart actions without its page-level controls (view switcher, rename, mode
 * switcher, help guide). Rail toggles sit on the side they open.
 */
export const EmbeddedChartToolbar: React.FC<EmbeddedChartToolbarProps> = ({
  leftOpen,
  rightOpen,
  modelsOpen,
  onToggleLeft,
  onToggleRight,
  onToggleModels,
  showRails,
  canSave,
  chartAreaRef,
}) => {
  const { t } = useLingui()

  const { chartName, chartData, xAxisSensorId } = useScatterplotContext()
  const pointToolDisabled = !!xAxisSensorId || chartData.length === 0
  const pointToolReason = xAxisSensorId
    ? t`Point annotations require time on the x-axis`
    : chartData.length === 0
      ? t`Nothing plotted yet`
      : t`Point Annotation`
  const layout: WorkbenchLayout = {
    left: leftOpen,
    right: rightOpen,
    bottom: false,
    dockMax: false,
  }
  const title = chartName?.trim()

  return (
    <Toolbar size='default' border='bottom'>
      <ToolbarSection>
        {showRails && (
          <LayoutToggles
            layout={layout}
            onToggle={onToggleLeft}
            sides={['left']}
          />
        )}
        {title && <ToolbarTitle>{title}</ToolbarTitle>}
      </ToolbarSection>
      <ToolbarSpacer />
      <ToolbarSection>
        {canSave && <ChartSaveAction appearance='toolbar' />}
        <PenDisplayActions appearance='toolbar' />
        <MarkupToolButtons
          appearance='toolbar'
          pointDisabled={pointToolDisabled}
          pointDisabledReason={pointToolReason}
        />
        {showRails && (
          <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
            <ChartTooltip label={t`Models`} side='bottom'>
              <Button
                variant='inputRaised'
                size='icon-lg'
                pressed='emphasis'
                aria-pressed={modelsOpen}
                aria-label={t`Models`}
                onClick={onToggleModels}>
                <Waypoints size={18} strokeWidth={1.5} />
              </Button>
            </ChartTooltip>
          </PermissionsGate>
        )}
        <ExportPngButton
          appearance='toolbar'
          getCanvas={() => chartAreaRef.current?.querySelector('canvas')}
        />
        {showRails && (
          <LayoutToggles
            layout={layout}
            onToggle={onToggleRight}
            sides={['right']}
          />
        )}
      </ToolbarSection>
    </Toolbar>
  )
}
